import { useMemo, useState } from 'react';
import type { StakingMode } from '../../analytics/staking.ts';
import { exposurePreview, validateProfitInputs } from '../../analytics/profit-preview.ts';
import type { ProfitCalculationView } from '../../analytics/profit-view.ts';
import { DROUGHT_ANALYTICS_TARGETS } from '../../shared/constants.ts';
import {
  formatDecimal,
  formatInteger,
  formatPercent,
  formatSignedUnits,
  formatUnits,
} from '../../shared/format.ts';
import { fetchJson } from '../lib/api.ts';
import { EntryStopMatrixSection } from '../components/EntryStopMatrixSection.tsx';
import { EquityChart } from '../components/EquityChart.tsx';
import { StatusMessage } from '../components/StatusMessage.tsx';

const DEFAULT_FORM = {
  targetMultiplier: 2,
  entryDrought: 6,
  stopDrought: 13,
  stakingMode: 'MARTINGALE' as StakingMode,
  baseStake: 1,
  targetEpisodeProfit: 1,
};

export function ProfitCalculatorPage() {
  const [targetMultiplier, setTargetMultiplier] = useState(DEFAULT_FORM.targetMultiplier);
  const [entryDrought, setEntryDrought] = useState(String(DEFAULT_FORM.entryDrought));
  const [stopDrought, setStopDrought] = useState(String(DEFAULT_FORM.stopDrought));
  const [stakingMode, setStakingMode] = useState<StakingMode>(DEFAULT_FORM.stakingMode);
  const [baseStake, setBaseStake] = useState(String(DEFAULT_FORM.baseStake));
  const [targetEpisodeProfit, setTargetEpisodeProfit] = useState(String(DEFAULT_FORM.targetEpisodeProfit));
  const [result, setResult] = useState<ProfitCalculationView | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [section, setSection] = useState<'single' | 'matrix'>('single');

  const parsed = {
    targetMultiplier,
    entryDrought: Number(entryDrought),
    stopDrought: Number(stopDrought),
    stakingMode,
    baseStake: Number(baseStake),
    targetEpisodeProfit: Number(targetEpisodeProfit),
  };
  const validation = validateProfitInputs(parsed);
  const preview = useMemo(() => {
    if (validation) {
      return null;
    }
    try {
      return exposurePreview(parsed);
    } catch {
      return null;
    }
  }, [parsed.targetMultiplier, parsed.entryDrought, parsed.stopDrought, parsed.stakingMode, parsed.baseStake, parsed.targetEpisodeProfit, validation]);

  const modes: StakingMode[] =
    targetMultiplier === 2 ? ['MARTINGALE', 'RECOVERY', 'FIXED'] : ['RECOVERY', 'FIXED'];

  function changeTarget(next: number) {
    setTargetMultiplier(next);
    setResult(null);
    if (next !== 2 && stakingMode === 'MARTINGALE') {
      setStakingMode('RECOVERY');
    }
  }

  async function calculate() {
    if (validation) {
      setError(validation);
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const body = await fetchJson<ProfitCalculationView>('/api/analytics/profit', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(parsed),
      });
      setResult(body);
    } catch (cause) {
      setResult(null);
      setError(cause instanceof Error ? cause.message : 'Calculation failed');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="space-y-6">
      <header>
        <h1 className="text-lg font-semibold">Profit calculator</h1>
        <p className="mt-1 text-sm text-muted">Replay a defined strategy against the selected historical dataset.</p>
        <div className="mt-3 flex flex-wrap gap-1" role="tablist" aria-label="Profit calculator sections">
          <button
            type="button"
            role="tab"
            aria-selected={section === 'single'}
            className={`rounded border px-3 py-1 text-sm ${
              section === 'single' ? 'border-ink bg-soft font-medium' : 'border-line bg-page text-muted'
            }`}
            onClick={() => setSection('single')}
          >
            Single strategy
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={section === 'matrix'}
            className={`rounded border px-3 py-1 text-sm ${
              section === 'matrix' ? 'border-ink bg-soft font-medium' : 'border-line bg-page text-muted'
            }`}
            onClick={() => setSection('matrix')}
          >
            Entry / Stop Matrix
          </button>
        </div>
      </header>

      {section === 'matrix' ? <EntryStopMatrixSection /> : null}

      {section === 'single' ? (
        <>
      <form
        className="grid gap-4 rounded border border-line bg-panel p-4 md:grid-cols-3"
        onSubmit={(event) => {
          event.preventDefault();
          void calculate();
        }}
      >
        <label className="flex flex-col gap-1 text-sm">
          Target
          <select
            className="rounded border border-line bg-page px-2 py-2"
            value={targetMultiplier}
            onChange={(event) => changeTarget(Number(event.target.value))}
          >
            {DROUGHT_ANALYTICS_TARGETS.map((value) => (
              <option key={value} value={value}>
                {value}x
              </option>
            ))}
          </select>
        </label>
        <label className="flex flex-col gap-1 text-sm">
          Entry drought D
          <input
            className="rounded border border-line bg-page px-2 py-2"
            inputMode="numeric"
            value={entryDrought}
            onChange={(event) => setEntryDrought(event.target.value)}
          />
        </label>
        <label className="flex flex-col gap-1 text-sm">
          Stop S
          <input
            className="rounded border border-line bg-page px-2 py-2"
            inputMode="numeric"
            value={stopDrought}
            onChange={(event) => setStopDrought(event.target.value)}
          />
        </label>
        <label className="flex flex-col gap-1 text-sm">
          Staking mode
          <select
            className="rounded border border-line bg-page px-2 py-2"
            value={stakingMode}
            onChange={(event) => setStakingMode(event.target.value as StakingMode)}
          >
            {modes.map((mode) => (
              <option key={mode} value={mode}>
                {mode}
              </option>
            ))}
          </select>
        </label>
        <label className="flex flex-col gap-1 text-sm">
          Base stake
          <input
            className="rounded border border-line bg-page px-2 py-2"
            inputMode="decimal"
            value={baseStake}
            onChange={(event) => setBaseStake(event.target.value)}
          />
        </label>
        {stakingMode === 'RECOVERY' ? (
          <label className="flex flex-col gap-1 text-sm">
            Target episode profit
            <input
              className="rounded border border-line bg-page px-2 py-2"
              inputMode="decimal"
              value={targetEpisodeProfit}
              onChange={(event) => setTargetEpisodeProfit(event.target.value)}
            />
          </label>
        ) : (
          <div />
        )}
        {validation ? (
          <p className="text-sm text-warn md:col-span-3" role="alert">
            {validation}
          </p>
        ) : null}
        <div className="md:col-span-3">
          <button
            type="submit"
            className="rounded bg-accent px-4 py-2 text-sm text-panel disabled:opacity-60"
            disabled={busy || Boolean(validation)}
          >
            {busy ? 'Calculating…' : 'Calculate'}
          </button>
        </div>
      </form>

      {preview && stakingMode === 'MARTINGALE' && targetMultiplier === 2 ? (
        <section className="rounded border border-line bg-panel p-4">
          <h2 className="text-xs font-semibold uppercase tracking-wide text-muted">Exposure preview</h2>
          <dl className="mt-3 grid gap-3 sm:grid-cols-4">
            <PreviewStat label="Attempts" value={formatInteger(preview.attempts)} />
            <PreviewStat label="Stake progression" value={preview.stakes.map((stake) => formatDecimal(stake, 2)).join(' → ')} />
            <PreviewStat label="Full stop-out" value={formatSignedUnits(-preview.fullStopExposure)} />
            <PreviewStat label="Largest wager" value={formatUnits(preview.largestWager)} />
          </dl>
        </section>
      ) : null}

      {error ? <StatusMessage kind="error">{error}</StatusMessage> : null}

      {result && result.completedEpisodes === 0 ? (
        <StatusMessage kind="empty">
          {`No completed historical episodes match these parameters. Current configured entry D=${result.inputs.entryDrought}. Open episodes=${formatInteger(result.openEpisodes)}; censored episodes=${formatInteger(result.censoredEpisodes)}.`}
        </StatusMessage>
      ) : null}

      {result && result.completedEpisodes > 0 ? (
        <ResultsPanel result={result} />
      ) : null}
        </>
      ) : null}
    </div>
  );
}

function PreviewStat(props: { label: string; value: string }) {
  return (
    <div>
      <dt className="text-xs text-muted">{props.label}</dt>
      <dd className="text-sm font-medium">{props.value}</dd>
    </div>
  );
}

function ResultsPanel(props: { result: ProfitCalculationView }) {
  const result = props.result;
  return (
    <div className="space-y-4">
      <section className="rounded border border-line bg-panel p-4">
        <h2 className="text-xs font-semibold uppercase tracking-wide text-muted">Historical simulation</h2>
        <p className="mt-1 text-xs text-muted">{result.note}</p>
        <dl className="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5">
          <EmphasisStat label="Net P&L" value={formatSignedUnits(result.netPnl)} tone={result.netPnl} />
          <EmphasisStat
            label="ROI"
            value={result.roi === null ? '—' : formatPercent(result.roi, 1)}
            tone={result.roi}
          />
          <EmphasisStat label="Stop-outs" value={formatInteger(result.stopOuts)} />
          <EmphasisStat label="Maximum drawdown" value={formatUnits(result.maximumCashflowDrawdown)} />
          <EmphasisStat label="Largest wager" value={formatUnits(result.largestIndividualWager)} />
        </dl>
        <dl className="mt-4 grid grid-cols-2 gap-x-4 gap-y-2 text-sm sm:grid-cols-4">
          <div>
            <dt className="text-xs text-muted">Completed episodes</dt>
            <dd>{formatInteger(result.completedEpisodes)}</dd>
          </div>
          <div>
            <dt className="text-xs text-muted">Successful episodes</dt>
            <dd>{formatInteger(result.successfulEpisodes)}</dd>
          </div>
          <div>
            <dt className="text-xs text-muted">Historical success rate</dt>
            <dd>{formatPercent(result.historicalEpisodeSuccessRate, 1)}</dd>
          </div>
          <div>
            <dt className="text-xs text-muted">Total wagers</dt>
            <dd>{formatInteger(result.totalWagers)}</dd>
          </div>
          <div>
            <dt className="text-xs text-muted">Amount wagered</dt>
            <dd>{formatUnits(result.totalAmountWagered)}</dd>
          </div>
          <div>
            <dt className="text-xs text-muted">Gross returned</dt>
            <dd>{formatUnits(result.grossReturned)}</dd>
          </div>
          <div>
            <dt className="text-xs text-muted">Maximum episode exposure</dt>
            <dd>{formatUnits(result.maximumEpisodeExposure)}</dd>
          </div>
          <div>
            <dt className="text-xs text-muted">Theoretical full-stop exposure</dt>
            <dd>{formatUnits(result.theoreticalFullStopExposure)}</dd>
          </div>
          <div>
            <dt className="text-xs text-muted">Open episodes</dt>
            <dd>{formatInteger(result.openEpisodes)}</dd>
          </div>
          <div>
            <dt className="text-xs text-muted">Censored episodes</dt>
            <dd>{formatInteger(result.censoredEpisodes)}</dd>
          </div>
        </dl>
      </section>

      <section className="rounded border border-line bg-panel p-4">
        <h2 className="text-xs font-semibold uppercase tracking-wide text-muted">Attempt distribution</h2>
        <div className="table-scroll mt-3">
          <table className="min-w-[420px] w-full text-left text-sm">
            <thead className="border-b border-line text-xs text-muted">
              <tr>
                <th className="px-3 py-2 font-medium">Attempt</th>
                <th className="px-3 py-2 font-medium">Stake</th>
                <th className="px-3 py-2 font-medium">Historical wins on this attempt</th>
                <th className="px-3 py-2 font-medium">Percentage of successful episodes</th>
              </tr>
            </thead>
            <tbody>
              {result.attemptRows.map((row) => (
                <tr key={row.attempt} className="border-b border-line/70">
                  <td className="px-3 py-2">{row.attempt}</td>
                  <td className="px-3 py-2">{formatUnits(row.stake)}</td>
                  <td className="px-3 py-2">{formatInteger(row.historicalWins)}</td>
                  <td className="px-3 py-2">{formatPercent(row.percentOfSuccessful, 1)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      {result.chronologicalPnl.length > 0 ? (
        <section className="rounded border border-line bg-panel p-4">
          <h2 className="text-xs font-semibold uppercase tracking-wide text-muted">Historical cumulative P&L</h2>
          <div className="mt-3">
            <EquityChart points={result.chronologicalPnl} />
          </div>
        </section>
      ) : null}
    </div>
  );
}

function EmphasisStat(props: { label: string; value: string; tone?: number | null }) {
  const toneClass =
    props.tone === undefined || props.tone === null || props.tone === 0
      ? ''
      : props.tone > 0
        ? 'text-positive'
        : 'text-negative';
  return (
    <div>
      <dt className="text-xs text-muted">{props.label}</dt>
      <dd className={`text-base font-semibold ${toneClass}`}>{props.value}</dd>
    </div>
  );
}
